import { Paginator, Platform } from '@app/classes';
import { Account } from '@app/models/account/account.model';
import { Email } from '@app/models/account/email.model';
import { Table } from '@app/modules/table/classes/table.class';
import { MenuColumnItem } from '@app/modules/table/interfaces/menu-column-item.interface';
import { AuthService, SettingsService } from '@app/services';
import { finalize, takeWhile } from 'rxjs/operators';
import { AddressColumn } from './columns/address.column';
import { StatusColumn } from './columns/status.column';

export type EmailsTableEvent = 'resend' | 'delete' | 'primary';

const MENU_COLUMN_OPTIONS: { [key: string]: MenuColumnItem<EmailsTableEvent> } = {
    resend: { label: 'account.resendVerification', event: 'resend', forceMeatballsDisplay: true },
    delete: { label: 'account.delete', event: 'delete', forceMeatballsDisplay: true },
    primary: { label: 'account.makePrimary', event: 'primary', forceMeatballsDisplay: true },
} as const;

export class AccountEmailsTable extends Table {
    showMenuColumnOptions = true;

    private isViewingOwnProfile = this.auth.account?.id === this.account.id;
    private isAdmin = this.auth.can(Platform.permission.administrate);
    private enforceSingleSignOn = false;

    constructor(
        protected auth: AuthService,
        protected settingsService: SettingsService,
        private account: Account
    ) {
        super(auth);
        this.boot();
        this.setSingleSignOnSetting();
    }

    setColumns(): void {
        this.addColumns(new AddressColumn(), new StatusColumn());
    }

    setDataSource(): void {
        this.paginator = new Paginator(Email.param('account', this.account.id));
    }

    getMenuColumnOptionsForRow(email: Email): MenuColumnItem<EmailsTableEvent>[] {
        if (email.isPrimary) {
            return [];
        }

        const items = [];

        if (!email.isVerified && this.isViewingOwnProfile) {
            items.push(MENU_COLUMN_OPTIONS.resend);
        }

        // if enforceSingleSignOn is true
        // we don't allow non-admins to modify emails
        if (this.enforceSingleSignOn && !this.isAdmin) {
            return items;
        }

        items.push(MENU_COLUMN_OPTIONS.delete);

        if ((email.isVerified && this.isViewingOwnProfile) || (this.isAdmin && !this.isViewingOwnProfile)) {
            items.push(MENU_COLUMN_OPTIONS.primary);
        }

        return items;
    }

    private setSingleSignOnSetting(): void {
        this.settingsService
            .onLoading()
            .pipe(
                takeWhile((isLoading) => isLoading), // unsubscribe when settingsService is done loading
                finalize(() => {
                    // once done loading, determine whether the user has SSO enforced
                    this.enforceSingleSignOn = this.settingsService.getByName('enforceSingleSignOn')?.value;
                })
            )
            .subscribe();
    }
}
